import { StockTotal } from '@Components/StorageStock/StockTotal';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { EnumStorageTransactionType } from '@Model';
import {
    Button,
    DateInputBase,
    FloatingCard,
    FlexRow,
    type SelectOption,
    SingleSelect,
    StyledParagraph,
    Table,
    iconSizeXs,
    FlexColumn,
    renderDateTime,
} from '@synqo/frontend-component-library';
import { IconPlus } from '@tabler/icons-react';
import { memo, useMemo, useState } from 'react';
import { useOpenStorageJournal } from '../useOpenStorageJournal';
import { avvStockColumns, materialStockColumns } from './stockColumns';
import { groupStorageStock, type StockRow, type StockView } from './stockView';
import { useStorageStock } from './useStorageStock';

type BookHandler = (type?: EnumStorageTransactionType, onBooked?: () => void) => void;

interface IProps {
    storageLocationId: string;
    storageLocationName: string;
    onBook: BookHandler;
}

const viewOptions: SelectOption<StockView>[] = [
    { name: 'material', value: 'material', label: 'Material' },
    { name: 'avv', value: 'avv', label: 'AVV' },
];

// @ai-generated 2026-10-23T14:50:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const StorageLocationStockSection = memo(function StorageLocationStockSection({
    storageLocationId,
    storageLocationName,
    onBook,
}: IProps) {
    const canBook = useHasPermission()(Permission.AuftragsbearbeitungStorageTransactionsCreate);
    const openJournal = useOpenStorageJournal();
    const [view, setView] = useState<StockView>('material');
    const [validAt, setValidAt] = useState<string | undefined>();

    const shown = useStorageStock({ storageLocationId, validAt });
    useRemoteCacheInvalidation('storage_transactions', shown.reload);
    const isInitialLoad = !validAt && shown.isLoading;
    const isEmpty = !validAt && !shown.isLoading && !shown.failure && shown.entries.length === 0;

    const grouped = useMemo(() => groupStorageStock(shown.entries, view), [shown.entries, view]);

    const stocktake = () => onBook(EnumStorageTransactionType.Stocktaking, shown.reload);

    const selectRow = (row: StockRow) => {
        openJournal({ id: storageLocationId, name: storageLocationName }, view, row);
        return true;
    };

    const bookButton = canBook && !validAt && (
        <Button size="xs" onClick={() => onBook(undefined, shown.reload)}>
            <IconPlus size={iconSizeXs} />
            &nbsp;Buchen
        </Button>
    );

    return (
        <FloatingCard
            title={validAt ? `Bestand zum ${renderDateTime(validAt, 'datetime')}` : 'Bestand'}
            separateHeader
            collapsible={false}
            additionalHeaderItems={bookButton}
        >
            {isInitialLoad ? null : (
                <>
                    <FlexRow align="end">
                        {!isEmpty && (
                            <SingleSelect<StockView>
                                name="stock_view"
                                items={viewOptions}
                                value={view}
                                onChange={(next) => {
                                    setView(next);
                                    return true;
                                }}
                            />
                        )}
                        <FlexColumn width="13rem" shrink={false} align="stretch">
                            <DateInputBase
                                name="stock_stichtag"
                                label="Stichtag"
                                type="datetime"
                                placeholder="jetzt"
                                clearable
                                value={validAt ?? ''}
                                onChange={(next) => {
                                    setValidAt(next || undefined);
                                    return true;
                                }}
                            />
                        </FlexColumn>
                    </FlexRow>
                    {shown.failure && <StyledParagraph>{shown.failure}</StyledParagraph>}
                    {isEmpty ? (
                        <EmptyStock canBook={canBook} onStocktaking={stocktake} />
                    ) : (
                        <>
                            <Table<StockRow>
                                name="StorageLocationStock"
                                columns={view === 'material' ? materialStockColumns : avvStockColumns}
                                items={grouped.rows}
                                queryKey={['StorageLocationStock', view]}
                                onSelect={selectRow}
                                disableTopToolbar
                                minimal
                            />
                            <StockTotal total={grouped.total} />
                        </>
                    )}
                </>
            )}
        </FloatingCard>
    );
});

interface IEmptyProps {
    canBook: boolean;
    onStocktaking: () => void;
}

// @ai-generated 2026-10-23T14:50:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function EmptyStock({ canBook, onStocktaking }: IEmptyProps) {
    return (
        <FlexColumn>
            <StyledParagraph>Noch kein Bestand. Den Anfangsbestand erfassen Sie per Inventur.</StyledParagraph>
            {canBook && <Button onClick={onStocktaking}>Inventur erfassen</Button>}
        </FlexColumn>
    );
}
